import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, Check, Trash2, ArrowRightLeft, Compass, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAIFilterStore } from '../../store/aiFilterStore';
import {
  Header,
  SearchBar,
  Chip,
  ListItem,
  FAB,
  EmptyState,
  Card,
  Button,
} from '../../components/ui';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { athletesApi } from '../../api/athletes';
import { groupsApi } from '../../api/groups';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import { formatAge } from '../../utils/dateUtils';

// -- Helpers ------------------------------------------------------------------

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLORS = [
  '#F57C00', // var(--avatar-1)
  '#E65100', // var(--avatar-2)
  '#FB8C00', // var(--avatar-3)
  '#FF9800', // var(--avatar-4)
  '#FFA726', // var(--avatar-5)
  '#FFB74D', // var(--avatar-6)
];

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// -- Loading skeleton ---------------------------------------------------------

const Skeleton: React.FC = () => (
  <div className="animate-pulse space-y-3 px-4 pt-6">
    <div className="h-8 rounded w-40" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    <div className="h-9 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }} />
    <div className="flex gap-2 mt-2">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-8 rounded-full w-20"
          style={{ backgroundColor: 'var(--bg-secondary)' }}
        />
      ))}
    </div>
    {[0, 1, 2, 3, 4].map((i) => (
      <div
        key={i}
        className="h-16 rounded"
        style={{ backgroundColor: 'var(--bg-secondary)' }}
      />
    ))}
  </div>
);

// -- Create form in BottomSheet -----------------------------------------------

interface CreateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; phone: string; parent_phone: string }) => void;
  isCreating: boolean;
}

const CreateAthleteSheet: React.FC<CreateFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isCreating,
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [parentPhone, setParentPhone] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), phone, parent_phone: parentPhone });
    setName('');
    setPhone('');
    setParentPhone('');
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Добавить спортсмена" height="80%">
      <div className="space-y-4 pt-4">
        {/* Name */}
        <div>
          <label
            className="block text-[13px] font-medium mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            ФИО *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Иванов Иван Иванович"
            disabled={isCreating}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Phone */}
        <div>
          <label
            className="block text-[13px] font-medium mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            Телефон
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+7 (999) 123-45-67"
            disabled={isCreating}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Parent phone */}
        <div>
          <label
            className="block text-[13px] font-medium mb-1"
            style={{ color: 'var(--text-secondary)' }}
          >
            Телефон родителя
          </label>
          <input
            type="tel"
            value={parentPhone}
            onChange={(e) => setParentPhone(e.target.value)}
            placeholder="+7 (999) 123-45-67"
            disabled={isCreating}
            className="w-full h-11 px-3 rounded-xl border-none outline-none text-[17px]"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
          />
        </div>

        {/* Actions */}
        <div className="pt-2 pb-4">
          <Button
            variant="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={!name.trim()}
            loading={isCreating}
          >
            Создать
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

// -- Main component -----------------------------------------------------------

export function AthletesScreen() {
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const { pushScreen } = useTabNavigationStore();
  const queryClient = useQueryClient();
  const aiFilterIds = useAIFilterStore((s) => s.filterIds);
  const aiFilterLabel = useAIFilterStore((s) => s.filterLabel);
  const clearAIFilter = useAIFilterStore((s) => s.clearFilter);

  const { maxAthletes } = useSubscriptionLimits();

  // -- Local state -----------------------------------------------------------

  const [search, setSearch] = useState('');
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string>('all');
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showMoveSheet, setShowMoveSheet] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // -- Data queries ----------------------------------------------------------

  const { data: athletes, isLoading: isLoadingAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const { data: groups } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  // -- Create mutation -------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: (data: { name: string; phone: string; parent_phone: string }) =>
      athletesApi.create({
        assistant_id: currentAssistantId!,
        name: data.name,
        phone: data.phone || undefined,
        parent_phone: data.parent_phone || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athletes', currentAssistantId] });
      setShowCreateSheet(false);
    },
    onError: (error) => {
      console.error('Failed to create athlete:', error);
      alert('Не удалось создать спортсмена. Попробуйте еще раз.');
    },
  });

  // -- Groups map for quick lookup -------------------------------------------

  const groupsMap = useMemo(() => {
    if (!groups) return new Map<string, string>();
    return new Map(groups.map((g) => [g.id, g.name]));
  }, [groups]);

  // -- Filtering -------------------------------------------------------------

  const filteredAthletes = useMemo(() => {
    if (!athletes) return [];

    // Если активен AI-фильтр — показываем только выбранных AI спортсменов
    let baseList = athletes;
    if (aiFilterIds && aiFilterIds.length > 0) {
      const filterSet = new Set(aiFilterIds);
      baseList = athletes.filter((a) => filterSet.has(a.id));
    }

    return baseList.filter((athlete) => {
      const matchesSearch = athlete.name
        .toLowerCase()
        .includes(search.toLowerCase());

      let matchesGroup = true;
      if (selectedGroupFilter === 'no-group') {
        matchesGroup = !athlete.group_id;
      } else if (selectedGroupFilter !== 'all') {
        matchesGroup = athlete.group_id === selectedGroupFilter;
      }

      return matchesSearch && matchesGroup;
    });
  }, [athletes, search, selectedGroupFilter, aiFilterIds]);

  // -- Derived state ---------------------------------------------------------

  const currentCount = athletes?.length ?? 0;
  const canCreateMore = maxAthletes === -1 || currentCount < maxAthletes;

  // -- Selection mode -------------------------------------------------------

  const toggleSelectionMode = useCallback(() => {
    if (isSelectionMode) {
      // Exiting selection mode — clear selection
      setSelectedIds(new Set());
    }
    setIsSelectionMode((prev) => !prev);
  }, [isSelectionMode]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectedIds(new Set());
    setIsSelectionMode(false);
  }, []);

  // -- Bulk actions ----------------------------------------------------------

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const confirmMsg = count === 1
      ? 'Удалить выбранного спортсмена?'
      : `Удалить выбранных спортсменов (${count})?`;
    if (!confirm(confirmMsg)) return;

    setIsBulkProcessing(true);
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) => athletesApi.delete(id))
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        alert(`Не удалось удалить ${failed} из ${count} спортсменов`);
      }
      queryClient.invalidateQueries({ queryKey: ['athletes', currentAssistantId] });
      exitSelectionMode();
    } catch {
      alert('Ошибка при удалении');
    } finally {
      setIsBulkProcessing(false);
    }
  }, [selectedIds, currentAssistantId, queryClient, exitSelectionMode]);

  const handleBulkMove = useCallback(async (groupId: string) => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;

    setIsBulkProcessing(true);
    setShowMoveSheet(false);
    try {
      const results = await Promise.allSettled(
        Array.from(selectedIds).map((id) =>
          athletesApi.update(id, { group_id: groupId })
        )
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        alert(`Не удалось перенести ${failed} из ${count} спортсменов`);
      }
      queryClient.invalidateQueries({ queryKey: ['athletes', currentAssistantId] });
      exitSelectionMode();
    } catch {
      alert('Ошибка при переносе');
    } finally {
      setIsBulkProcessing(false);
    }
  }, [selectedIds, currentAssistantId, queryClient, exitSelectionMode]);

  // -- Loading ---------------------------------------------------------------

  if (isLoadingAthletes) {
    return <Skeleton />;
  }

  // -- No assistant selected -------------------------------------------------

  if (!currentAssistantId) {
    return (
      <EmptyState
        icon={Compass}
        title="Направление не выбрано"
        description="Выберите или создайте направление, чтобы начать работу со спортсменами"
        primaryAction={{
          label: 'Выбрать направление',
          onClick: () => {
            try {
              window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
            } catch {}
            // Open direction switcher
            const { openDirectionSwitcher } = useTabNavigationStore.getState();
            openDirectionSwitcher();
          },
        }}
      />
    );
  }

  // -- Empty state -----------------------------------------------------------

  if (!athletes || athletes.length === 0) {
    return (
      <>
        <EmptyState
          icon={Users}
          title="Нет спортсменов"
          description="Добавьте первого спортсмена вручную или импортируйте список из файла"
          primaryAction={
            canCreateMore
              ? {
                  label: 'Добавить спортсмена',
                  onClick: () => setShowCreateSheet(true),
                }
              : undefined
          }
          secondaryAction={{
            label: 'Импортировать список',
            onClick: () =>
              pushScreen('import-athletes', {
                assistantId: currentAssistantId,
              }),
          }}
        />
        <CreateAthleteSheet
          isOpen={showCreateSheet}
          onClose={() => setShowCreateSheet(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isCreating={createMutation.isPending}
        />
      </>
    );
  }

  // -- List ------------------------------------------------------------------

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      <div className="px-4 pt-4">
        {/* Header */}
        <Header
          title="Спортсмены"
          action={{
            label: isSelectionMode ? 'Готово' : 'Выбрать',
            onClick: toggleSelectionMode,
          }}
        />

        {/* AI Filter Banner */}
        {aiFilterIds && aiFilterLabel && (
          <div
            className="flex items-center justify-between px-3 py-2 rounded-xl mb-2"
            style={{
              backgroundColor: 'var(--accent-bg, rgba(245,124,0,0.1))',
              border: '1px solid var(--accent)',
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[14px]">🤖</span>
              <span
                className="text-[14px] font-medium truncate"
                style={{ color: 'var(--accent)' }}
              >
                {aiFilterLabel}
              </span>
              <span
                className="text-[13px] flex-shrink-0"
                style={{ color: 'var(--text-hint)' }}
              >
                ({aiFilterIds.length})
              </span>
            </div>
            <button
              onClick={clearAIFilter}
              className="flex-shrink-0 p-1 rounded-full"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              <X size={16} color="var(--text-hint)" />
            </button>
          </div>
        )}

        {/* Search */}
        <SearchBar
          placeholder="Поиск по имени..."
          value={search}
          onChange={setSearch}
        />

        {/* Group filter chips */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
          <Chip
            label="Все"
            active={selectedGroupFilter === 'all'}
            onClick={() => setSelectedGroupFilter('all')}
          />
          {groups?.map((group) => (
            <Chip
              key={group.id}
              label={group.name}
              active={selectedGroupFilter === group.id}
              onClick={() => setSelectedGroupFilter(group.id)}
            />
          ))}
          <Chip
            label="Без группы"
            active={selectedGroupFilter === 'no-group'}
            onClick={() => setSelectedGroupFilter('no-group')}
          />
        </div>
      </div>

      {/* Athlete list */}
      <div className="mt-4">
        <Card className="!p-0 !rounded-none mx-4 !rounded-xl overflow-hidden">
          {filteredAthletes.map((athlete) => {
            const groupName = athlete.group_id
              ? groupsMap.get(athlete.group_id)
              : null;
            const age = formatAge(athlete.date_of_birth);
            const subtitleParts: string[] = [];
            if (groupName) subtitleParts.push(groupName);
            if (age) subtitleParts.push(age);

            const isSelected = selectedIds.has(athlete.id);

            return (
              <ListItem
                key={athlete.id}
                avatar={getInitials(athlete.name)}
                avatarBg={getAvatarColor(athlete.id)}
                title={athlete.name}
                subtitle={subtitleParts.join(' \u00B7 ') || undefined}
                onClick={
                  isSelectionMode
                    ? () => toggleSelect(athlete.id)
                    : () => pushScreen('athlete-detail', { athleteId: athlete.id })
                }
                rightElement={
                  isSelectionMode ? (
                    <div
                      className="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                      style={{
                        borderColor: isSelected ? 'var(--accent)' : 'var(--text-hint)',
                        backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
                      }}
                    >
                      {isSelected && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
                    </div>
                  ) : undefined
                }
              />
            );
          })}
        </Card>

        {/* No search results */}
        {filteredAthletes.length === 0 && search && (
          <div className="py-12 text-center">
            <p
              className="text-[15px]"
              style={{ color: 'var(--text-hint)' }}
            >
              Спортсмены не найдены
            </p>
          </div>
        )}

        {/* Limit banner */}
        {!canCreateMore && (
          <div className="mx-4 mt-4">
            <Card>
              <p
                className="text-[13px]"
                style={{ color: 'var(--text-hint)' }}
              >
                Создано {currentCount} из {maxAthletes} спортсменов.{' '}
                <button
                  onClick={() => pushScreen('subscription-manage' as any)}
                  className="underline"
                  style={{ color: 'var(--accent)' }}
                >
                  Обновите тариф
                </button>{' '}
                для добавления.
              </p>
            </Card>
          </div>
        )}

        {/* TODO: Export / Import buttons row */}

      </div>

      {/* Selection action bar — fixed at bottom */}
      {isSelectionMode && selectedIds.size > 0 && (
        <div
          className="fixed bottom-[72px] left-0 right-0 z-30 px-4 pb-2"
        >
          <div
            className="rounded-2xl px-4 py-3 flex items-center justify-between"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
            }}
          >
            <span
              className="text-[15px] font-medium"
              style={{ color: 'var(--text-primary)' }}
            >
              Выбрано: {selectedIds.size}
            </span>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMoveSheet(true)}
                disabled={isBulkProcessing}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[15px] font-medium transition-opacity disabled:opacity-40"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF',
                }}
              >
                <ArrowRightLeft size={16} />
                В группу
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={isBulkProcessing}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[15px] font-medium transition-opacity disabled:opacity-40"
                style={{
                  backgroundColor: '#FF3B30',
                  color: '#FFFFFF',
                }}
              >
                <Trash2 size={16} />
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB — hide in selection mode */}
      {canCreateMore && !isSelectionMode && (
        <FAB
          onClick={() => setShowCreateSheet(true)}
          icon={<Plus size={28} color="#FFFFFF" strokeWidth={2.5} />}
        />
      )}

      {/* Create Sheet */}
      <CreateAthleteSheet
        isOpen={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isCreating={createMutation.isPending}
      />

      {/* Move to Group Sheet */}
      <BottomSheet
        isOpen={showMoveSheet}
        onClose={() => setShowMoveSheet(false)}
        title="Перенести в группу"
        height="60%"
      >
        <div className="pt-2">
          {groups && groups.length > 0 ? (
            <div className="space-y-1">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleBulkMove(group.id)}
                  disabled={isBulkProcessing}
                  className="w-full text-left px-4 py-3 rounded-xl flex items-center justify-between transition-opacity disabled:opacity-40"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <span className="text-[17px]">{group.name}</span>
                  <span
                    className="text-[13px]"
                    style={{ color: 'var(--text-hint)' }}
                  >
                    {selectedIds.size} чел.
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p
                className="text-[15px]"
                style={{ color: 'var(--text-hint)' }}
              >
                Нет доступных групп
              </p>
            </div>
          )}
        </div>
      </BottomSheet>
    </div>
  );
}
